import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { SelectComponent, SelectOption as LibSelectOption } from '@libs/front/components/select/select.component';
import {
  DataTableComponent,
  TableCellDirective,
  TableColumn,
} from '@libs/front/components/data-table/data-table.component';
import { ScrollableComponent } from 'src/app/shared/components/scrollable/scrollable.component';

import { ControlPoint } from 'src/app/bo/controlPoint';
import { VibrationData } from 'src/app/bo/vibrationsForChart/vibration-data';
import { VibrationRecords } from 'src/app/bo/vibrationsForChart/vibration-records';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { VibrationsRecordService } from 'src/app/services/vibrations-record.service';

type RecordTab = 'manual' | 'upload';

/** Mini-graphe SVG d'un canal — remplace le canvas Chart.js de l'ancienne modale. */
interface ChannelSparkline {
  label: string;
  points: string;
  min: number;
  max: number;
  /** Graduations de l'axe des temps, réparties sur la largeur du graphe. */
  ticks: string[];
}

/** Ligne du tableau : propriétés plates pour que le tri natif du tableau fonctionne. */
interface UploadedRow {
  id: number;
  record: VibrationData;
  fileName: string;
  /** Taille du fichier importé, affichée sous son nom. */
  sizeLabel: string;
  timestampMs: number;
  dateLabel: string;
  timeLabel: string;
  notes: string;
  deviceLabel: string;
  /** Valeur absolue maximale par canal, telle qu'affichée dans la colonne « Maximum ». */
  channelMaxes: { label: string; value: number }[];
  /** Clé de tri de la colonne « Maximum ». */
  maxPpv: number;
  sparklines: ChannelSparkline[];
}

/** Extensions acceptées par l'import de fichiers d'enregistrement. */
const ALLOWED_EXTENSIONS = ['.csv', '.sis'];

/**
 * Refonte de `app-record-modal` : même logique (saisie manuelle d'un enregistrement ou
 * import de fichiers .sis/.csv puis validation), rendue avec les composants génériques de
 * `@libs/front/components` et pilotée par `[isOpen]` au lieu de `MatDialog`.
 */
@Component({
  selector: 'app-blast-record-modal',
  templateUrl: './blast-record-modal.component.html',
  styleUrls: ['./blast-record-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    PopupComponent,
    ButtonComponent,
    InputComponent,
    SelectComponent,
    ScrollableComponent,
    DataTableComponent,
    TableCellDirective,
  ],
})
export class BlastRecordModalComponent {
  readonly isOpen = input<boolean>(false);
  readonly siteId = input.required<number>();
  readonly blastId = input<number>();
  readonly eventId = input<number | null>(null);
  readonly controlPointId = input<number | null>(null);
  readonly controlPointName = input<string | null>(null);
  readonly siteName = input<string>('');
  readonly regionName = input<string | undefined>(undefined);
  readonly controlPoints = input<ControlPoint[]>([]);

  readonly closed = output<void>();
  /** Émis après un enregistrement réussi : le parent recharge sa liste. */
  readonly saved = output<void>();

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  // ── État ───────────────────────────────────────────────────────────────────

  protected readonly activeTab = signal<RecordTab>('manual');
  protected readonly errorMessage = signal('');

  /** Fichiers déposés, cumulés comme dans l'ancienne modale. */
  protected readonly files = signal<File[]>([]);
  /** Enregistrements renvoyés par l'import, en attente de validation. */
  protected readonly records = signal<VibrationData[]>([]);
  protected readonly uploadProgress = signal<number | null>(null);
  protected readonly isUploading = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly isManualSubmitting = signal(false);

  private readonly recordSevice = inject(VibrationsRecordService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(FormBuilder);

  protected readonly manualForm: FormGroup = this.fb.group({
    controlPointId: [null, [Validators.required, Validators.min(1)]],
    distance: [null, [Validators.required, Validators.min(0)]],
    overpressure: [null, [Validators.required, Validators.min(0)]],
    pic: [null, [Validators.required, Validators.min(0)]],
  });

  protected readonly tabs: { key: RecordTab; label: string; icon: string }[] = [
    { key: 'manual', label: 'BUTTONS.MANUAL_ENTRY', icon: 'assets/icon/manual.svg' },
    { key: 'upload', label: 'BUTTONS.UPLOAD_FILE', icon: 'assets/icon/file.svg' },
  ];

  protected readonly controlPointOptions = computed<LibSelectOption[]>(() =>
    this.controlPoints().map(cp => ({ id: cp.controlPointId, label: cp.name })),
  );

  protected readonly rows = computed<UploadedRow[]>(() =>
    this.records().map((record, index) => this.toRow(record, index)),
  );

  /**
   * Les cinq premières colonnes forment la première ligne de la carte, `remove` porte le ✕
   * et `sparklines` passe à la ligne suivante sur toute la largeur (cf. SCSS).
   */
  protected readonly tableColumns: TableColumn[] = [
    { key: 'fileName', label: 'FORM.FILE_NAME', width: '22%' },
    { key: 'timestampMs', label: 'FORM.DATE_AND_TIME', width: '15%' },
    { key: 'notes', label: 'VIBRATION.NOTE', sortable: false, width: '13%' },
    { key: 'maxPpv', label: 'VIBRATION.MAX', sortable: false, width: '24%' },
    { key: 'deviceLabel', label: 'FORM.DEVICES', width: '20%' },
    { key: 'remove', label: '', sortable: false, width: '6%' },
    { key: 'sparklines', label: '', sortable: false },
  ];

  constructor() {
    // Le dialog n'existait qu'ouvert : chaque ouverture repart d'un état vierge.
    effect(() => {
      if (!this.isOpen()) return;
      this.resetState();
    });
  }

  // ── Onglets ────────────────────────────────────────────────────────────────

  protected selectTab(tab: RecordTab): void {
    this.activeTab.set(tab);
    this.errorMessage.set('');
  }

  // ── Saisie manuelle ────────────────────────────────────────────────────────

  protected isFieldInvalid(name: string): boolean {
    const control = this.manualForm.get(name);
    return !!control && control.invalid && control.touched;
  }

  protected submitManualRecord(): void {
    if (this.manualForm.invalid) {
      this.manualForm.markAllAsTouched();
      return;
    }
    this.isManualSubmitting.set(true);

    const { controlPointId, distance, overpressure, pic } = this.manualForm.value;
    const payload = {
      controlPointId: +controlPointId,
      distance: +distance,
      overpressure: +overpressure,
      pic: +pic,
      siteId: this.siteId(),
      blastId: this.blastId(),
    };

    this.recordSevice.saveManualRecord(this.siteId(), payload).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true);
        this.saved.emit();
        this.closed.emit();
      },
      error: () => {
        this.isManualSubmitting.set(false);
        this.fonctionsService.launch_toast(false);
      },
    });
  }

  // ── Import de fichiers ─────────────────────────────────────────────────────

  /** La zone de dépôt entière est cliquable : on neutralise le clic du bouton interne. */
  protected openFilePicker(event?: MouseEvent): void {
    event?.stopPropagation();
    this.fileInput()?.nativeElement.click();
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files) this.handleFiles(event.dataTransfer.files);
  }

  protected onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files) this.handleFiles(input.files);
    // Permet de re-sélectionner le même fichier après un retrait
    if (input) input.value = '';
  }

  private handleFiles(fileList: FileList): void {
    const incoming = Array.from(fileList);
    const accepted = incoming.filter(file => this.isFileAllowed(file.name));

    if (accepted.length !== incoming.length) {
      this.errorMessage.set(`${this.translate.instant('MESSAGE_INFO.ACCEPTED_FILE_FORMATS')}: sis, csv`);
    } else {
      this.errorMessage.set('');
    }
    if (!accepted.length) return;

    this.files.update(current => [...current, ...accepted]);
    this.uploadFiles();
  }

  /** Retire un enregistrement de la sélection, ainsi que le fichier dont il provient. */
  protected removeRecord(row: UploadedRow): void {
    this.records.update(current => current.filter(record => record !== row.record));
    if (row.fileName) {
      this.files.update(current => current.filter(file => file.name !== row.fileName));
    }
    this.errorMessage.set('');
  }

  /** Un seul envoi pour l'ensemble des fichiers retenus, comme l'attend le back. */
  private uploadFiles(): void {
    const files = this.files();
    if (!files.length) return;

    const formData = new FormData();
    files.forEach(file => formData.append('files', file, file.name));

    this.isUploading.set(true);
    this.uploadProgress.set(0);

    this.recordSevice.UploadFilenRecords(this.siteId(), formData).subscribe({
      next: (event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            if (event.total) {
              this.uploadProgress.set(Math.round((event.loaded / event.total) * 100));
            }
            break;

          case HttpEventType.Response: {
            const uploaded: VibrationData[] = event.body?.data ?? [];
            uploaded.forEach(record => {
              record.eventId = this.eventId() as number;
              record.controlPointId = this.controlPointId() as number;
            });
            this.records.set(uploaded);
            this.isUploading.set(false);
            this.uploadProgress.set(null);
            break;
          }
        }
      },
      error: () => {
        this.isUploading.set(false);
        this.uploadProgress.set(null);
        this.errorMessage.set(this.translate.instant('ERRORS_MESSAGE.UPLOAD_FAILED'));
      },
    });
  }

  private isFileAllowed(fileName: string): boolean {
    const extension = /(?:\.([^.]+))?$/.exec(fileName)?.[0] ?? '';
    return ALLOWED_EXTENSIONS.includes(extension.toLowerCase());
  }

  protected confirmUpload(): void {
    const records = this.records();
    if (!records.length) {
      this.errorMessage.set(this.translate.instant('ERRORS_MESSAGE.NO_RECORD_TO_UPLOAD'));
      return;
    }

    records.forEach(record => (record.blastId = this.blastId()));
    const payload: VibrationRecords = {
      information: {
        currentURL: window.location.href,
        regionName: this.regionName(),
        siteName: this.siteName(),
        controlPointName: this.controlPointName() as string,
      },
      vibrationRecords: records,
    };

    this.isSaving.set(true);
    this.recordSevice.SaveRecords(this.siteId(), payload).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true);
        this.saved.emit();
        this.closed.emit();
      },
      error: () => {
        this.isSaving.set(false);
        this.fonctionsService.launch_toast(false);
      },
    });
  }

  // ── Fermeture ──────────────────────────────────────────────────────────────

  protected closeDialog(): void {
    this.closed.emit();
  }

  protected closeErrorMessage(): void {
    this.errorMessage.set('');
  }

  private resetState(): void {
    this.activeTab.set('manual');
    this.errorMessage.set('');
    this.files.set([]);
    this.records.set([]);
    this.uploadProgress.set(null);
    this.isUploading.set(false);
    this.isSaving.set(false);
    this.isManualSubmitting.set(false);
    this.manualForm.reset();
  }

  // ── View model ─────────────────────────────────────────────────────────────

  private toRow(record: VibrationData, index: number): UploadedRow {
    const meta = record.recordMetadata;
    const [dateLabel, timeLabel] = this.formatTimestamp(meta?.timestamp);
    const channels = record.signalDataEntity?.signalData?.channels ?? [];

    const channelMaxes = channels.map((channel, i) => ({
      label: `C${i + 1}`,
      value: this.findMaxAbsValue(channel.timeSpectrum.ppvMin, channel.timeSpectrum.ppvMax),
    }));

    return {
      id: index,
      record,
      fileName: meta?.fileName ?? '',
      sizeLabel: this.formatFileSize(this.files().find(file => file.name === meta?.fileName)?.size),
      timestampMs: meta?.timestamp ? new Date(meta.timestamp).getTime() : 0,
      dateLabel,
      timeLabel,
      notes: meta?.note1 ?? '',
      deviceLabel: meta?.seismographSerialNumber ?? '',
      channelMaxes,
      maxPpv: channelMaxes.reduce((max, channel) => Math.max(max, Math.abs(channel.value)), 0),
      sparklines: channels
        .map((_, i) => this.toSparkline(record, i))
        .filter((spark): spark is ChannelSparkline => spark !== null),
    };
  }

  public findMaxAbsValue(min: number, max: number): number {
    return Math.abs(max) > Math.abs(min) ? max : Math.abs(min);
  }

  /** Canal entièrement à zéro : l'ancienne modale ne dessinait pas de graphe. */
  private toSparkline(record: VibrationData, channelIndex: number): ChannelSparkline | null {
    const channel = record.signalDataEntity?.signalData?.channels?.[channelIndex];
    const raw = channel?.timeSpectrum?.values;
    if (!channel || !raw?.length) return null;
    if (raw.every(value => value === 0)) return null;

    const max = channel.timeSpectrum.ppvMax;
    const min = channel.timeSpectrum.ppvMin;
    const bound = Math.max(Math.abs(max), Math.abs(min)) || 1;

    const width = 120;
    const height = 40;
    const step = raw.length > 120 ? Math.floor(raw.length / 120) : 1;
    const sampled: number[] = [];
    for (let i = 0; i < raw.length; i += step) sampled.push(raw[i] as number);

    const points = sampled
      .map((value, i) => {
        const x = (i / (sampled.length - 1 || 1)) * width;
        const y = height / 2 - (value / bound) * (height / 2 - 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

    const samplingFrequency = record.recordMetadata?.samplingPeriod;
    const totalSeconds = samplingFrequency ? raw.length / samplingFrequency : 0;
    const ticks = [0, totalSeconds / 3, (totalSeconds / 3) * 2, totalSeconds].map(s => `${s.toFixed(0)}s`);

    return { label: `C${channelIndex + 1}`, points, min, max, ticks };
  }

  private formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    const megabytes = bytes / (1024 * 1024);
    if (megabytes >= 1) return `${megabytes.toFixed(megabytes < 10 ? 1 : 0)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  private formatTimestamp(ts?: string): [string, string] {
    const parts = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(ts ?? '');
    if (!parts) return ['-', '-'];
    const [, year, month, day, hours, minutes, seconds] = parts;
    return [`${day}/${month}/${year}`, `${hours}:${minutes}:${seconds ?? '00'}`];
  }
}
